import { Download } from 'lucide-react';
import { Badge, Button, Select, formatNumber } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { TFunction } from 'i18next';
import { choiceOf, choicesFor, withTarget, type Permissions } from './column-choices.ts';
import {
  maps,
  mappingProblems,
  templateCsv,
  type ImportLocale,
  type ImportReport,
  type ImportView,
  type MappedColumn,
  type Preview,
} from './import-model.ts';
import styles from './ImportSheet.module.css';

export type MappingStepProps = {
  preview: Preview;
  columns: readonly MappedColumn[];
  defaultLocale: ImportLocale;
  permissions: Permissions;
  depots: readonly { readonly id: string; readonly name: string }[];
  locationId: string | null;
  onColumns: (columns: MappedColumn[]) => void;
  onLocation: (locationId: string | null) => void;
};

const choiceLabel = (t: TFunction, choice: string): string => {
  const [field = choice, locale] = choice.split(':');
  return locale === undefined
    ? t(`productImport.field_${field}`)
    : t(`productImport.field_${field}_in`, { locale: locale.toUpperCase() });
};

export const MappingStep = ({
  preview,
  columns,
  defaultLocale,
  permissions,
  depots,
  locationId,
  onColumns,
  onLocation,
}: MappingStepProps) => {
  const { t } = useTranslation();
  const problems = mappingProblems(columns, defaultLocale);
  const choices = choicesFor(permissions);
  return (
    <div className={styles['stack']}>
      <p className={styles['help']}>{t('productImport.mappingHelp')}</p>
      <ul className={styles['columns']} aria-label={t('productImport.columns')}>
        {preview.headers.map((header, index) => (
          <li key={`${String(index)}-${header}`} className={styles['column']}>
            <div>
              <strong className={styles['header']}>
                {header === '' ? t('productImport.unnamedColumn', { position: String(index + 1) }) : header}
              </strong>
              <span className={styles['sample']}>
                {preview.rows
                  .slice(0, 2)
                  .map((row) => row[index] ?? '')
                  .filter((value) => value !== '')
                  .join(' · ') || '—'}
              </span>
            </div>
            <Select
              label={t('productImport.importAs', { column: header })}
              value={choiceOf(columns.find((column) => column.index === index)?.target)}
              options={choices.map((choice) => ({ value: choice, label: choiceLabel(t, choice) }))}
              onChange={(event) => {
                onColumns(withTarget(columns, index, header, event.target.value));
              }}
            />
          </li>
        ))}
      </ul>
      {maps(columns, 'stock') ? (
        <Select
          label={t('productImport.depot')}
          value={locationId ?? ''}
          options={[
            { value: '', label: t('productImport.chooseDepot') },
            ...depots.map((depot) => ({ value: depot.id, label: depot.name })),
          ]}
          onChange={(event) => {
            onLocation(event.target.value === '' ? null : event.target.value);
          }}
        />
      ) : null}
      {problems.length === 0 ? null : (
        <ul role="alert" className={styles['problems']}>
          {problems.map((problem) => (
            <li key={problem}>
              {t(`productImport.problem_${problem}`, { locale: defaultLocale.toUpperCase() })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const canCheck = (
  columns: readonly MappedColumn[],
  defaultLocale: ImportLocale,
  locationId: string | null,
): boolean =>
  mappingProblems(columns, defaultLocale).length === 0 && (!maps(columns, 'stock') || locationId !== null);

export const ReportStep = ({ report, locale }: { report: ImportReport; locale: string }) => {
  const { t } = useTranslation();
  return (
    <div className={styles['stack']}>
      <p role="status" className={styles['summary']}>
        <Badge tone={report.products > 0 ? 'success' : 'warning'}>
          {t('productImport.ready', { count: formatNumber(report.products, locale) })}
        </Badge>
        {report.errorCount === 0 ? null : (
          <Badge tone="danger">
            {t('productImport.toFix', { count: formatNumber(report.errorCount, locale) })}
          </Badge>
        )}
        <span>{t('productImport.rowsRead', { count: formatNumber(report.rows, locale) })}</span>
      </p>
      {report.errors.length === 0 ? null : (
        <ol className={styles['issues']} aria-label={t('productImport.issues')}>
          {report.errors.map((issue) => (
            <li key={`${String(issue.row)}-${issue.column ?? ''}-${issue.code}`} className={styles['issue']}>
              <span className={styles['where']}>
                {t('productImport.issueWhere', {
                  row: formatNumber(issue.row, locale),
                  column: issue.column ?? '—',
                })}
              </span>
              <strong>{t(`productImport.error_${issue.code}`)}</strong>
              {issue.value === null ? null : (
                <span className={styles['value']} dir="auto">
                  {issue.value}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {report.errorCount > report.errors.length ? (
        <p className={styles['help']}>
          {t('productImport.moreErrors', {
            count: formatNumber(report.errorCount - report.errors.length, locale),
          })}
        </p>
      ) : null}
    </div>
  );
};

export const ResultStep = ({ view, locale }: { view: ImportView; locale: string }) => {
  const { t } = useTranslation();
  const failures = view.report?.failures ?? [];
  return (
    <div className={styles['stack']}>
      <p role="status" className={styles['summary']}>
        <Badge tone={view.status === 'completed' ? 'success' : 'danger'}>
          {view.status === 'completed'
            ? t('productImport.created', { count: formatNumber(view.createdCount, locale) })
            : t('productImport.failed')}
        </Badge>
      </p>
      <p className={styles['help']}>{t('productImport.draftsHelp')}</p>
      {failures.length === 0 ? null : (
        <ul className={styles['problems']} aria-label={t('productImport.failures')}>
          {failures.map((failure) => (
            <li key={`${failure.reference}-${failure.stage}`}>
              {t('productImport.failure', {
                reference: failure.reference,
                stage: t(`productImport.stage_${failure.stage}`),
                reason: t(`errors.${failure.code}`, { defaultValue: t('errors.UNKNOWN') }),
              })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const TemplateButton = () => {
  const { t } = useTranslation();
  return (
    <Button
      icon={<Download size={16} aria-hidden="true" />}
      variant="ghost"
      size="sm"
      onClick={downloadTemplate}
    >
      {t('productImport.template')}
    </Button>
  );
};

const TEMPLATE_NAME = 'modele-import-produits.csv';

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([templateCsv()], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = TEMPLATE_NAME;
  link.click();
  URL.revokeObjectURL(url);
};
